import {
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useListen,
    useProp,
} from '@atomify/hooks';
import { inviewObserver } from '@utilities/inview';
import EmblaCarousel, { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';

export interface SliderInterface extends HTMLElement {
    select: (index: number, options?: { jump?: boolean }) => void;
}

const SHOW_FADE_START = 'slider-embla--fade-start';
const SHOW_FADE_END = 'slider-embla--fade-end';

const SliderEmbla: FC<SliderInterface> = ({ element }) => {
    const [centered] = useProp<boolean>('centered', false);
    const [sliderOptions] = useProp<string | null>('sliderOptions', null);
    const [controlMode] = useProp<string | null>('controlMode', null);
    const [showFade] = useProp<boolean>('showFade', false);
    const [onSelect] = useProp<Function | null>('onSelect', null);

    const slider = useElement<HTMLElement>('[js-hook-slider]');
    const sliderPrevious = useElement<HTMLElement | null>('[js-hook-slider-control-previous]');
    const sliderNext = useElement<HTMLElement | null>('[js-hook-slider-control-next]');

    let embla: EmblaCarouselType | undefined;
    let slideNodes: HTMLElement[];
    let currentSlideIndex: number;
    let isInView: boolean = false;

    useListen(sliderNext, 'click', next);
    function next() {
        if (controlMode === 'start-end') {
            embla?.scrollTo(slideNodes.length - 1);
        } else {
            embla?.scrollNext();
        }
    }

    useListen(sliderPrevious, 'click', previous);
    function previous() {
        if (controlMode === 'start-end') {
            embla?.scrollTo(0);
        } else {
            embla?.scrollPrev();
        }
    }

    function setSelectedState() {
        if (!embla) return;

        if (!sliderNext || !sliderPrevious) return;

        if (!embla.canScrollNext()) {
            sliderNext.current?.setAttribute('disabled', 'disabled');
        } else {
            sliderNext.current?.removeAttribute('disabled');
        }

        if (!embla.canScrollPrev()) {
            sliderPrevious.current?.setAttribute('disabled', 'disabled');
        } else {
            sliderPrevious.current?.removeAttribute('disabled');
        }
    }

    function setFade() {
        if (!embla || !slideNodes) return;

        const slidesNotInView = embla.slidesNotInView();
        const currScrollSnap = embla.selectedScrollSnap();
        const lastSlideIndex = slideNodes.length - 1;

        if (slidesNotInView.includes(0) && currScrollSnap > 0) {
            element.classList.add(SHOW_FADE_START);
        } else {
            element.classList.remove(SHOW_FADE_START);
        }

        if (slidesNotInView.includes(lastSlideIndex) && currScrollSnap < lastSlideIndex) {
            element.classList.add(SHOW_FADE_END);
        } else {
            element.classList.remove(SHOW_FADE_END);
        }
    }

    useBindMethod('select', select);
    function select(index: number, options?: { jump?: boolean }) {
        if (currentSlideIndex !== index) {
            currentSlideIndex = index;
            embla?.scrollTo(index, options?.jump);
        }
    }

    function checkKey(e: KeyboardEvent) {
        if (isInView) {
            if (e.key == 'ArrowLeft' || e.key == 'ArrowUp') {
                previous();
            } else if (e.key == 'ArrowRight' || e.key == 'ArrowDown') {
                next();
            }
        }
    }

    function initSlider() {
        const emblaNode = slider.current;
        if (!emblaNode) return;

        const emblaOptions: EmblaOptionsType = {
            align: 'start',
            containScroll: 'trimSnaps',
            inViewThreshold: 1,
            ...(sliderOptions && (JSON.parse(sliderOptions) as EmblaOptionsType)),
        };

        if (centered) {
            emblaOptions.align = 'center';
            emblaOptions.containScroll = 'keepSnaps';
        }

        embla = EmblaCarousel(emblaNode, emblaOptions);

        slideNodes = embla?.slideNodes();

        if (embla) {
            embla.on('select', () => {
                setSelectedState();
                onSelect && onSelect(embla?.selectedScrollSnap());
            });

            if (showFade) {
                embla.on('settle', () => {
                    setFade();
                });
            }
        }

        slideNodes.forEach((slideNode, index) => {
            slideNode.addEventListener('click', () => {
                select(index);
            });
        });
    }

    function destroySlider() {
        if (embla) {
            embla.destroy();
            embla = undefined;
        }
    }

    function setupSlider() {
        if (!embla) {
            initSlider();
        } else {
            destroySlider();
        }
    }

    onDidLoad(() => {
        if (!slider.current) return;
        inviewObserver.observe(slider.current, (inView) => {
            isInView = inView;
        });

        window.addEventListener('keydown', (e: KeyboardEvent) => {
            if (isInView) checkKey(e);
        });

        setupSlider();
        setSelectedState();
        showFade && setFade();
    });
};

SliderEmbla.props = {
    centered: { type: Boolean },
    sliderOptions: { type: String },
    controlMode: { type: String },
    showFade: { type: Boolean },
    onSelect: { type: Function },
};

defineElement('slider-embla', SliderEmbla);
